import type { RouteStop, TripMode } from '@/types';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { findPath } from './pathfinding';
import { haversine } from './geo';
import { CO2_TRAIN, CO2_BUS } from './constants';

/**
 * Totaux indépendants du calendrier : recalcule l'itinéraire dans un mode donné,
 * sans tenir compte de la date de départ.
 * Utilisé par le mini-compare "Train vs Bus vs Mixte" pour évaluer chaque scénario.
 */

export interface ModeTotals {
  mode: TripMode;
  totalMin: number;
  totalKm: number;
  totalSegs: number;
  totalDirects: number;
  trainMin: number;
  busMin: number;
  co2: number;
  unreachable: boolean;
}

export function computeRouteTotals(stops: RouteStop[], mode: TripMode): ModeTotals {
  let totalMin = 0;
  let totalKm = 0;
  let totalSegs = 0;
  let totalDirects = 0;
  let trainMin = 0;
  let busMin = 0;
  let unreachable = false;

  for (let i = 0; i < stops.length - 1; i++) {
    const stop = stops[i];
    const next = stops[i + 1];
    const s = STATIONS[STATION_INDEX[stop.id]];
    const nextS = STATIONS[STATION_INDEX[next.id]];
    totalKm += haversine(s.coords, nextS.coords);

    const path = findPath(stop.id, next.id, mode);
    if (!path) {
      unreachable = true;
      continue;
    }

    totalMin += path.total;
    totalSegs++;
    if (path.segments.length === 1) totalDirects++;

    path.segments.forEach((seg) => {
      if (seg.mode === 'train') trainMin += seg.cost;
      else if (seg.mode === 'bus') busMin += seg.cost;
    });
  }

  const totalModeMin = Math.max(1, trainMin + busMin);
  const co2 =
    trainMin > 0 || busMin > 0
      ? Math.round(
          totalKm * (trainMin / totalModeMin) * CO2_TRAIN +
            totalKm * (busMin / totalModeMin) * CO2_BUS,
        )
      : 0;

  return {
    mode,
    totalMin,
    totalKm,
    totalSegs,
    totalDirects,
    trainMin,
    busMin,
    co2,
    unreachable,
  };
}
